/**
 * The galaxy map's Minerals layer (M8 plan MC-a7b, RD7(a)): a `MapOverlayLayer` over MC-a7a's knowledge model
 * (`exploration/minerals.ts mineralsKnowledge`). Only charted systems are styled: the node takes the colour and size
 * of the richest asteroid deposit's grade and a badge with the signature ore(s), and the tooltip names the signature,
 * the richest grade and one flavour line. An uncharted system keeps the map's plain style and its tooltip says
 * "Uncharted". All text, colours and sizes live in `mineralsMap.json` (Zod-checked). Pure over the Reach state and
 * the galaxy, read on each call, so a system charted while the map is open shows on the map's next rebuild (it
 * rebuilds on Reach events). UI rate: allocates freely.
 */
import { z } from 'zod'
import { galaxyGradeIds, type GalaxyGradeId } from '../../data/galaxyConfig'
import { mineralsKnowledge, type ChartedMinerals } from '../../exploration/minerals'
import type { Galaxy, MineralOre, SystemSignature } from '../../galaxy/types'
import type { MapOverlayLayer, MapOverlayNodeStyle } from '../../maps/model/overlays'
import type { ReachState } from '../../reach/types'
import raw from './mineralsMap.json'

const color = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const size = z.number().gt(0).max(3)
const text = z.string().min(1)
const gradeStyle = z.strictObject({ name: text, color, size })
const perOre = <T extends z.ZodType>(v: T) =>
  z.strictObject({ ferrite: v, silicates: v, ice: v, titanium: v, platinum: v, refractory_ore: v })
const perGrade = <T extends z.ZodType>(v: T) => z.strictObject({ poor: v, standard: v, rich: v, pristine: v })

export const mineralsMapConfigSchema = z
  .strictObject({
    comment: z.string().optional(),
    layerId: text,
    label: text,
    grades: perGrade(gradeStyle),
    none: z.strictObject({ color, size }),
    oreGlyphs: perOre(text),
    oreNames: perOre(text),
    legend: z.strictObject({ poor: text, standard: text, rich: text, pristine: text, none: text }),
    tooltip: z.strictObject({ signature: text, richest: text, none: text, uncharted: text }),
    flavour: perGrade(text),
  })
  .superRefine((c, ctx) => {
    // Canon (D3-FC C3/C4): richness is access, never depletion.
    if (/stripped/i.test(JSON.stringify({ ...c, comment: '' }))) ctx.addIssue({ code: 'custom', message: 'minerals map copy must never say "stripped"' })
    const sizes = galaxyGradeIds.map((g) => c.grades[g].size)
    if (sizes.some((s, i) => i > 0 && s < sizes[i - 1])) ctx.addIssue({ code: 'custom', message: 'grade sizes must not fall' })
  })
export type MineralsMapConfig = z.infer<typeof mineralsMapConfigSchema>

let cached: MineralsMapConfig | null = null

export function loadMineralsMapConfig(data: unknown = raw): MineralsMapConfig {
  if (data !== raw) return mineralsMapConfigSchema.parse(data)
  cached ??= mineralsMapConfigSchema.parse(data)
  return cached
}

/** What the layer reads, on each call (null outside the Reach: every node plain). */
export interface MineralsWorld {
  galaxy: Galaxy
  state: ReachState
}

function signatureOres(sig: SystemSignature): MineralOre[] {
  const ores: MineralOre[] = sig.secondary === undefined ? [sig.primary] : [sig.primary, sig.secondary]
  return sig.refractory === true ? [...ores, 'refractory_ore'] : ores
}

/** The richest grade among a charted system's deposits, or null when it has none. */
export function richestGrade(m: ChartedMinerals): GalaxyGradeId | null {
  let best = -1
  for (const d of m.deposits) best = Math.max(best, galaxyGradeIds.indexOf(d.grade))
  return best < 0 ? null : galaxyGradeIds[best]
}

/** A charted system's node style: richest grade's colour and size, signature glyph badge; no deposit: `none`. */
export function mineralsNodeStyle(m: ChartedMinerals, cfg: MineralsMapConfig): MapOverlayNodeStyle {
  const grade = richestGrade(m)
  if (grade === null) return { color: cfg.none.color, size: cfg.none.size }
  const style = cfg.grades[grade]
  const out: MapOverlayNodeStyle = { color: style.color, size: style.size }
  if (m.signature !== null) out.badge = signatureOres(m.signature).map((o) => cfg.oreGlyphs[o]).join('+')
  return out
}

/** A charted system's tooltip lines: signature, richest grade, flavour; or the one "no deposits" line. */
export function mineralsTooltip(m: ChartedMinerals, cfg: MineralsMapConfig): string[] {
  const grade = richestGrade(m)
  if (grade === null && m.signature === null) return [cfg.tooltip.none]
  const lines: string[] = []
  if (m.signature !== null) {
    lines.push(cfg.tooltip.signature.replace('{ores}', signatureOres(m.signature).map((o) => cfg.oreNames[o]).join(' · ')))
  }
  if (grade !== null) {
    lines.push(cfg.tooltip.richest.replace('{grade}', cfg.grades[grade].name))
    lines.push(cfg.flavour[grade])
  }
  return lines
}

/** The Minerals layer over `world()` (read on each call). */
export function createMineralsLayer(world: () => MineralsWorld | null, cfg: MineralsMapConfig = loadMineralsMapConfig()): MapOverlayLayer {
  const charted = (systemId: string): ChartedMinerals | null => {
    const w = world()
    if (w === null) return null
    const k = mineralsKnowledge(w.state, w.galaxy, systemId)
    return k === 'uncharted' ? null : k
  }
  return {
    id: cfg.layerId,
    label: cfg.label,
    legend: [
      ...galaxyGradeIds.map((g) => ({ label: cfg.legend[g], color: cfg.grades[g].color })).reverse(),
      { label: cfg.legend.none, color: cfg.none.color },
    ],
    node(systemId: string): MapOverlayNodeStyle | null {
      const m = charted(systemId)
      return m === null ? null : mineralsNodeStyle(m, cfg)
    },
    edge: () => null,
    tooltip(systemId: string): readonly string[] {
      if (world() === null) return []
      const m = charted(systemId)
      return m === null ? [cfg.tooltip.uncharted] : mineralsTooltip(m, cfg)
    },
    picker: null,
  }
}
